import { Group, Stack, Switch } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Avatar, getPagingParams, ItemsPerPageType, Pagination, Select } from '@uag/react-core';
import { clsx } from 'clsx';
import { useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    GetTenantByIdResponse,
    GetTenantDetailsByIdResponse,
    ApiResponseOfBasicUserModel,
    UserState,
} from 'api/v3/models';
import { useSearchUsers } from 'api/v3/user/user';
import { listQueryDefault } from 'app/queryDefaults';
import { getItemLink } from 'modules/shared/functions';
import { PermissionProvider, usePermissions } from 'modules/shared/permissions';
import { PrincipalSelector } from 'modules/shared/PrincipalSelector';
import { OverviewGrid } from 'shared/components/OverviewGrid';
import { getTotalPages } from 'utils/paginations';
import { UserManagementBaseRoute } from '../Routes';
import { CreateUserModal } from './components/CreateUserModal';
import { DeactivateAction } from './components/DeactivateAction';
import { DeleteUserAction } from './components/DeleteUserAction';
import { ForceResetPasswordAction } from './components/ForceResetPasswordAction';
import { InviteUserModal } from './components/InviteUserModal';
import { ResendInviteAction } from './components/ResendInvitationAction';
import { ResetPasswordAction } from './components/ResetPasswordAction';
import { UndeleteUserAction } from './components/UndeleteUserAction';
import { UserStateIndicator } from './components/UserStateIndicator';
import { UsersBaseRoute } from './Routes';

type Props = {
    label?: string;
    name?: string;
    disabled?: boolean;
    tenant?: GetTenantDetailsByIdResponse | GetTenantByIdResponse;
    linkTarget?: 'NewWindow' | 'SameWindow';
};

export const Users = ({ tenant, linkTarget = 'SameWindow' }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [showSoftDeletedUsers, setShowSoftDeletedUsers] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState<ItemsPerPageType>(25);
    const [page, setPage] = useState(1);
    const [selectedState, setSelectedState] = useState<string | null>(null);
    const [selectedTenant, setSelectedTenant] = useState<string | undefined>(undefined);

    const [searchValue, setSearchValue] = useState<string>();
    const [debounceSearchValue] = useDebouncedValue(searchValue, 300);

    const {
        data: users,
        isLoading: areSearchUsersLoading,
        error: searchUsersError,
    } = useSearchUsers(
        {
            tenantId: tenant?.data.id ?? selectedTenant,
            searchTerm: debounceSearchValue,
            state: (selectedState as UserState) ?? undefined,
            includePermissions: true,
            includeProfilePictureUri: true,
            includeSoftDeleted: showSoftDeletedUsers,
            ...getPagingParams(page, itemsPerPage),
        },
        { ...listQueryDefault },
    );

    const totalPages = getTotalPages(itemsPerPage, users);

    const canInvite = usePermissions('Invite', 'User', tenant, users);
    const canInviteWithoutEmail = usePermissions('InviteWithoutEmail', 'User', tenant, users);

    const handleOnSearch = (text: string) => {
        setPage(1);
        setSearchValue(text);
    };

    const userStateValues = Object.values(UserState).map((state) => ({
        value: state,
        label: t(`userState.${state}`),
    }));

    const handleRowClicked = (_event: MouseEvent, item: ApiResponseOfBasicUserModel) => {
        if (item.data.id && item.data.state !== UserState.Deleted) {
            if (linkTarget === 'SameWindow') {
                navigate(`/${UserManagementBaseRoute}/${UsersBaseRoute}/${item.data.id}`);
            } else {
                window.open(getItemLink({ id: item.data.id, type: 'User' }));
            }
        }
    };

    return (
        <PermissionProvider context="User" items={[tenant, users]}>
            <Stack className={clsx('p-xl w-full', { ['card']: !tenant })}>
                {!tenant && <div className="text-4xl">{t('users')}</div>}
                <Group mb="sm" />
                <OverviewGrid
                    additionalRowActions={[
                        ResendInviteAction,
                        DeactivateAction,
                        ResetPasswordAction,
                        ForceResetPasswordAction,
                        DeleteUserAction,
                        UndeleteUserAction,
                    ]}
                    data={users?.data ?? []}
                    error={searchUsersError}
                    fieldDescriptions={[
                        {
                            header: '',
                            data: '',
                            width: 50,
                            lockUpdate: true,
                            render: (item) => <Avatar size="sm" src={item.data.profilePictureUri ?? undefined} />,
                        },
                        {
                            header: t('username'),
                            data: 'data.userName',
                            lockUpdate: true,
                        },
                        {
                            header: t('state'),
                            data: 'data.state',
                            lockUpdate: true,
                            render: (item) => <UserStateIndicator userState={item.data.state} />,
                        },
                        {
                            header: t('tenant'),
                            data: 'data.tenant.name',
                            lockUpdate: true,
                            link: (item) => getItemLink({ id: item.data.tenant.id, type: 'Tenant' }, 'users'),
                        },
                    ]}
                    filter={
                        <>
                            <Select
                                key={4}
                                className="pr-base"
                                data={userStateValues}
                                placeholder={t('selectUserState')}
                                value={selectedState}
                                allowDeselect
                                onChange={setSelectedState}
                            />
                            {!tenant?.data && (
                                <PrincipalSelector
                                    className="pr-base"
                                    placeholder={t('selectTenant')}
                                    principalTypeFilter={['Tenant']}
                                    allowDeselect
                                    onPrincipalSelected={(principal) => setSelectedTenant(principal?.id)}
                                />
                            )}
                        </>
                    }
                    headerActions={[
                        <Switch
                            key={2}
                            checked={showSoftDeletedUsers}
                            label={t('showItem', { itemType: t('softDeletedUsers') })}
                            onChange={(event) => {
                                setShowSoftDeletedUsers(event.currentTarget.checked);
                            }}
                        />,
                        canInvite && <InviteUserModal key={0} />,
                        canInviteWithoutEmail && <CreateUserModal key={1} />,
                    ]}
                    isLoading={areSearchUsersLoading}
                    onRowClicked={handleRowClicked}
                    onSearch={(text) => handleOnSearch(text)}
                />
                <Pagination
                    disabled={areSearchUsersLoading}
                    itemsPerPage={itemsPerPage}
                    page={page}
                    totalPages={totalPages}
                    withSkip
                    onItemsPerPageChange={setItemsPerPage}
                    onPageChange={setPage}
                />
            </Stack>
        </PermissionProvider>
    );
};
